import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ArrowRight, Plus } from 'lucide-react';
import FadeInSection from '../common/FadeInSection';
import axios from 'axios';

const EventSection = () => {
    const [filteredState, setFilteredState] = useState("VIC");
    const [activeTab, setActiveTab] = useState("communities"); // 'events' or 'communities'
    const [isAnimating, setIsAnimating] = useState(false);
    const [gardenEvents, setGardenEvents] = useState([]);
    const [filteredGardenEvents, setFilteredGardenEvents] = useState([]);
    const [gardenCommunities, setGardenCommunities] = useState([]);
    const [filteredGardenCommunities, setFilteredGardenCommunities] = useState([]);
    const [isLoading, setIsLoading] = useState(false);


    const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';

    // Fetch events when component mounts
    useEffect(() => {
        fetchEventData();
        fetchCommunityData();
    }, []);

      // Filter logic triggers with animation
      useEffect(() => {
        setIsAnimating(true);
        const timer = setTimeout(() => setIsAnimating(false), 500);
        
        filterGardenEvents();
        filterGardenCommunities();
    
        return () => clearTimeout(timer);
      }, [gardenEvents, gardenCommunities, filteredState]);

      // // Filter events by Australian states
  const filterGardenEvents = async () => {
    const filterCopy = [...gardenEvents];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalise to midnight for clean comparison

    const filteredEvents = filterCopy.filter(anEvent => {
      const eventDate = new Date(anEvent.event_enddate);
      eventDate.setHours(0, 0, 0, 0);

      // Match state AND only include events that end today or in the future
      return (
        filteredState === anEvent.event_state &&
        eventDate >= today
      );
    });

    // Sort by earliest date
    filteredEvents.sort((a, b) => new Date(a.event_enddate) - new Date(b.event_enddate));
    setFilteredGardenEvents(filteredEvents);
  };

  // Filter communities by Australian states
  const filterGardenCommunities = async () => {
    const filterCopy = [...gardenCommunities];
    console.log("filterCopy", filterCopy);

    const filteredCommunities = filterCopy.filter(aCommunity => {
      if (filteredState === aCommunity.community_state) {
        return true;
      }
      else {
        return false;
      }
    })
    setFilteredGardenCommunities(filteredCommunities)
  };

  // Fetch garden event data
  const fetchEventData = async () => {

    try {
      const response = await axios.get(`${API_BASE_URL}/database/tables/events/data`);

      setGardenEvents(response.data.sampleData);

    } catch (error) {
      console.error('Error fetching event data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch garden community data
  const fetchCommunityData = async () => {

    try {
      const response = await axios.get(`${API_BASE_URL}/database/tables/community/data`);

      setGardenCommunities(response.data.sampleData);

    } catch (error) {
      console.error('Error fetching community data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      day: d.getDate(),
      month: d.toLocaleString('default', { month: 'short' }).toUpperCase(),
      weekday: d.toLocaleString('default', { weekday: 'long' })
    };
  };

  return (
    <>
      <style>{`
        {/* @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700&display=swap'); */}

        :root {
          --moss-bg: #F2EBD4;
          --paper-bg: #FCFBF7;
          --accent-green: #496c1c;
          --accent-gold: #D68C21;
          --wood-brown: #8D6E63;
          --deep-earth: #3E2723;
        }

        .almanac-section {
          background-color: var(--moss-bg);
          padding: 6rem 2rem;
          position: relative;
          min-height: 800px;
        }

        /* Background Texture */
        .almanac-section::before {
          content: "";
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background-image: url("https://www.transparenttextures.com/patterns/cubes.png");
          opacity: 0.08;
          pointer-events: none;
        }

        .almanac-wrapper {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        /* --- HEADER --- */
        .header-row {
          display: flex;
          justify-content: space-evenly;
          align-items: flex-end;
          margin-bottom: 3rem; /* Spacing for tabs sticking up */
        }

        .header-title {
          font-family: 'Playfair Display', serif;
          font-size: 3rem;
          color: var(--accent-green);
          margin: 0;
          line-height: 1;
        }
        
        .header-subtitle {
          font-family: 'Lato', sans-serif;
          color: #666;
          margin-top: 10px;
        }

        /* Filter Dropdown Styled as a "Stamp" */
        .region-filter {
          background: white;
          border: 2px solid var(--accent-green);
          border-radius: 12px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 4px 4px 0 rgba(73, 108, 28, 0.2);
          transition: transform 0.2s;
        }
        .region-filter:hover {
          transform: translateY(-2px);
        }
        .filter-select {
          border: none;
          background: transparent;
          font-family: 'Playfair Display', serif;
          font-weight: 700;
          font-size: 1.1rem;
          color: var(--deep-earth);
          cursor: pointer;
          outline: none;
        }

        /* --- TABS (Plant Markers) --- */
        .tabs-container {
          display: flex;
          gap: 10px;
          padding-left: 2rem;
          transform: translateY(2px); /* Connect to folder */
          z-index: 1;
          position: relative;
        }

        .tab-marker {
          padding: 12px 30px 15px 30px;
          border-radius: 15px 15px 0 0;
          cursor: pointer;
          font-family: 'Lato', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          transition: all 0.3s ease;
          border: 1px solid rgba(0,0,0,0.05);
          border-bottom: none;
          position: relative;
        }

        .tab-marker.active {
          background-color: var(--paper-bg);
          color: var(--accent-green);
          box-shadow: 0 -5px 10px rgba(0,0,0,0.05);
        }

        .tab-marker.inactive {
          background-color: #f0f0f0;
          color: #888;
        }

        .tab-marker:hover {
          background-color: var(--paper-bg);
        }

        /* --- MAIN FOLDER CONTENT --- */
        .almanac-folder {
          background-color: var(--paper-bg);
          border-radius: 0 20px 20px 20px;
          box-shadow: 0 20px 50px rgba(73, 108, 28, 0.15);
          min-height: 500px;
          position: relative;
          overflow: hidden;
          z-index: 2;
          border-top: 4px solid var(--accent-green); /* The "Binding" */
        }

        /* The Paper Lines Texture */
        .folder-content {
          padding: 3rem;
          background-image: linear-gradient(#e5e5e5 1px, transparent 1px);
          background-size: 100% 2rem; /* Notebook lines */
          min-height: 500px;
        }

        /* Folder Corner Accent */
        .almanac-folder::after {
          content: "";
          position: absolute;
          bottom: 0; right: 0;
          width: 100px; height: 100px;
          background: linear-gradient(135deg, transparent 50%, rgba(214, 140, 33, 0.08) 50%);
          border-radius: 0 0 12px 0;
          pointer-events: none;
        }

        .folder-content {
          position: relative;
          z-index: 1;
        }

        /* --- EVENTS VIEW (Tickets) --- */
        .events-list-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .ticket-card {
          background: white;
          display: flex;
          align-items: stretch;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          transition: transform 0.3s, box-shadow 0.3s;
          border-left: 6px solid var(--accent-gold);
          position: relative;
          text-decoration: none;
        }

        .ticket-card:hover {
          transform: translateX(10px);
          box-shadow: 0 8px 25px rgba(73, 108, 28, 0.15);
        }

        /* Perforated line effect */
        .ticket-stub {
          background-color: var(--moss-bg);
          width: 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-right: 2px dashed #ccc;
          padding: 1rem;
          color: var(--accent-green);
        }

        .ticket-day { font-size: 2rem; font-weight: 800; font-family: 'Playfair Display', serif; line-height: 1; }
        .ticket-month { font-size: 0.9rem; font-weight: 700; letter-spacing: 1px; }

        .ticket-info {
          padding: 1.5rem;
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .ticket-title { font-family: 'Lato', sans-serif; font-weight: 700; font-size: 1.2rem; color: var(--deep-earth); margin-bottom: 0.5rem; }
        .ticket-loc { font-size: 0.9rem; color: #666; display: flex; align-items: center; gap: 5px; }
        
        .ticket-action {
          background: var(--moss-bg);
          padding: 10px;
          border-radius: 50%;
          color: var(--accent-green);
          transition: all 0.2s;
        }
        .ticket-card:hover .ticket-action { background: var(--accent-green); color: white; }

        /* --- COMMUNITIES VIEW (Polaroids) --- */
        .communities-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 280px));
          gap: 2rem;
          justify-content: center;
          max-width: 920px;
          margin: 0 auto;
        }

        .polaroid-card {
          background: white;
          padding: 1rem 1rem 2rem 1rem; /* Extra bottom padding for Polaroid look */
          box-shadow: 0 6px 15px rgba(0,0,0,0.08);
          transform: rotate(-1deg);
          transition: all 0.3s ease;
          text-decoration: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
        }

        .polaroid-card:nth-child(even) { transform: rotate(1deg); }
        
        .polaroid-card:hover {
          transform: scale(1.05) rotate(0deg);
          box-shadow: 0 15px 30px rgba(0,0,0,0.15);
          z-index: 10;
        }

        .polaroid-img-box {
          width: 100%;
          height: 160px;
          background: #f0f0f0;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #eee;
        }

        .polaroid-img { width: 60px; height: 60px; opacity: 0.8; }

        .polaroid-title {
          font-family: 'Lato', sans-serif;
          font-weight: 700;
          color: var(--deep-earth);
          text-align: center;
          margin-bottom: 0.5rem;
          font-size: 1rem;
        }
        
        .polaroid-meta { font-size: 0.8rem; color: #888; display: flex; align-items: center; gap: 5px; }

        /* --- CTA CARD --- */
        .cta-card {
          background: linear-gradient(135deg, #D68C21 0%, #E7A74E 100%);
          padding: 2rem 1.5rem;
          box-shadow: 0 8px 20px rgba(214, 140, 33, 0.25);
          transform: rotate(0deg);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .cta-card:hover {
          transform: scale(1.08) translateY(-5px);
          box-shadow: 0 15px 35px rgba(214, 140, 33, 0.4);
        }

        .cta-icon {
          font-size: 3.5rem;
          margin-bottom: 1rem;
          background: white;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #D68C21;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .cta-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.3rem;
          color: white;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .cta-description {
          font-family: 'Lato', sans-serif;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.95);
          line-height: 1.5;
          margin-bottom: 1rem;
        }

        .cta-button {
          margin-top: 0.5rem;
          padding: 10px 24px;
          background: white;
          color: #D68C21;
          border: none;
          border-radius: 50px;
          font-family: 'Lato', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }

        .cta-button:hover {
          background: #FEFDF5;
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
        }

        /* Empty State */
        .empty-state {
          grid-column: 1/-1;
          text-align: center;
          color: #888;
          padding: 3rem;
          font-family: 'Lato', sans-serif;
        }

        /* Animation Classes */
        .content-fade {
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.4s ease-out;
        }
        .content-fade.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .header-row { flex-direction: column; align-items: center; text-align: center; gap: 1rem; }
          .tabs-container { justify-content: center; padding-left: 0; }
          .ticket-card { flex-direction: column; }
          .ticket-stub { width: 100%; border-right: none; border-bottom: 2px dashed #ccc; flex-direction: row; gap: 10px; padding: 0.5rem; }
          .ticket-info { flex-direction: column; text-align: center; gap: 1rem; }
          .communities-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          }
        }
      `}</style>

      <div id="homepage-eventscomm-section" className="almanac-section">
        <div className="almanac-wrapper">
          
          {/* Header & Filter */}
          <FadeInSection>
            <div className="header-row">
              <div>
                <h2 className="header-title">Discover what's growing in your area.</h2>
                {/* <p className="header-subtitle">Discover what's growing in your area.</p> */}
              </div>

              <div className="region-filter">
                <MapPin size={18} color="#496c1c" />
                <select 
                  className="filter-select"
                  value={filteredState}
                  onChange={(e) => setFilteredState(e.target.value)}
                >
                  {['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {/* <ChevronDown size={16} color="#D68C21" /> */}
              </div>
            </div>
          </FadeInSection>

          {/* Tab Navigation (Plant Markers) */}
          <div className="tabs-container">
            <div 
              className={`tab-marker ${activeTab === 'events' ? 'active' : 'inactive'}`}
              onClick={() => setActiveTab('events')}
            >
              🌱 Upcoming Events
            </div>
            <div 
              className={`tab-marker ${activeTab === 'communities' ? 'active' : 'inactive'}`}
              onClick={() => setActiveTab('communities')}
            >
              🌻 Communities
            </div>
          </div>

          {/* Main Content Area (The Folder) */}
          <div className="almanac-folder">
            <div className="folder-content">
              
              {/* --- EVENTS TAB CONTENT --- */}
              {activeTab === 'events' && (
                <div className={`events-list-container content-fade ${!isAnimating ? 'visible' : ''}`}>
                  {filteredGardenEvents.length > 0 ? (
                    filteredGardenEvents.map((evt) => {
                      const date = formatDate(evt.date);
                      return (
                        <a key={evt.id} href={evt.link} target="_blank" rel="noreferrer" className="ticket-card">
                          <div className="ticket-stub">
                            <span className="ticket-day">{date.day}</span>
                            <span className="ticket-month">{date.month}</span>
                          </div>
                          <div className="ticket-info">
                            <div>
                              <div className="ticket-title">{evt.name}</div>
                              <div className="ticket-loc"><MapPin size={14} /> {evt.location}</div>
                            </div>
                            <div className="ticket-action">
                              <ArrowRight size={20} />
                            </div>
                          </div>
                        </a>
                      );
                    })
                  ) : (
                    <div className="empty-state">
                      No events scheduled in this region yet.
                    </div>
                  )}
                </div>
              )}

              {/* --- COMMUNITIES TAB CONTENT --- */}
              {activeTab === 'communities' && (
                <div className={`communities-grid content-fade ${!isAnimating ? 'visible' : ''}`}>
                  {filteredGardenCommunities.length > 0 ? (
                    <>
                      {filteredGardenCommunities.map((comm, i) => (
                        <a key={i} href={comm.community_link} target="_blank" rel="noreferrer" className="polaroid-card">
                          <div className="polaroid-img-box">
                            <img src={comm.icon_image_url} alt={comm.community_name} className="polaroid-img" />
                          </div>
                          <div className="polaroid-title">{comm.community_name}</div>
                        </a>
                      ))}
                      
                      {/* CTA Card */}
                      <div className="polaroid-card cta-card" onClick={() => {
                        // Add your navigation logic here
                        console.log('Navigate to community search or creation page');
                      }}>
                        <div className="cta-icon">
                          <Plus size={40} strokeWidth={2.5} />
                        </div>
                        <h3 className="cta-title">Join a Community</h3>
                        <p className="cta-description">Connect with gardeners in your area and share your passion</p>
                        <button className="cta-button">Explore More</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Show CTA card even when no communities */}
                      <div className="polaroid-card cta-card" onClick={() => {
                        console.log('Navigate to community search or creation page');
                      }}>
                        <div className="cta-icon">
                          <Plus size={40} strokeWidth={2.5} />
                        </div>
                        <h3 className="cta-title">Start a Community</h3>
                        <p className="cta-description">Be the first to create a gardening community in {filteredState}</p>
                        <button className="cta-button">Get Started</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default EventSection;